/**
 * App lock overlay (S03). Covers every route once the device locks or sits idle (idle-lock.ts
 * policy); only a user-verified passkey assertion unlocks. Nothing behind the overlay is focusable
 * or readable while it is shown.
 */
import { Button } from '@viko/transcript-ui'
import { Fingerprint, Lock, MonitorSmartphone } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { AppLockController, appLock } from '../lib/app-lock-controller.js'
import { useBackend } from '../lib/backend.js'
import { loadDevice } from '../lib/device-key.js'
import {
  isMobileDevice,
  passkeyUnlock,
  persistAppLock,
  shouldAutoPrompt,
} from '../lib/idle-lock.js'
import { passkeyErrorMessage, passkeys } from '../lib/passkeys.js'
import { loadPresenceBinding, tryPresenceUnlock } from '../lib/presence-unlock.js'
import { isSessionOnly } from '../lib/session-only.js'

export function AppLockView({
  onUnlock,
  busy,
  error,
  presence,
}: {
  onUnlock: () => void
  busy?: boolean
  error?: string | undefined
  /** passkey-budget WP1: this browser follows its computer's OS login. */
  presence?: boolean
}) {
  // Hide the app from assistive tech and keyboard focus while locked.
  useEffect(() => {
    const root = document.getElementById('root')
    root?.setAttribute('inert', '')
    root?.setAttribute('aria-hidden', 'true')
    return () => {
      root?.removeAttribute('inert')
      root?.removeAttribute('aria-hidden')
    }
  }, [])
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-lock-title"
      className="fixed inset-0 z-[1000] grid place-items-center bg-bg/95 px-4 backdrop-blur-xl"
    >
      <div className="grid max-w-[340px] justify-items-center gap-3 text-center">
        <Lock size={28} aria-hidden="true" className="text-text-3" />
        <h2 id="app-lock-title" className="m-0 text-[17px] font-semibold">
          Viko is locked
        </h2>
        <p className="m-0 text-[15px] text-text-2">
          Locked because this device locked, slept or sat idle. Unlock with your passkey to continue
          {presence ? ', or unlock your computer: Viko follows its login' : ''}.
        </p>
        <Button
          variant="primary"
          icon={<Fingerprint size={16} />}
          onClick={onUnlock}
          disabled={busy}
          autoFocus
        >
          Unlock with passkey
        </Button>
        {error ? (
          <p role="alert" className="m-0 text-[14px] text-status-fail">
            {error}
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

/**
 * One-time, explained request for the Idle Detection permission (Chromium desktop). A slim in-flow
 * banner above the page content (mounted by AppShell), never an overlay: the browser prompt only fires on the Allow click, and "Not now" is
 * remembered per device (You › Security can ask again).
 */
export function IdleDetectionPrompt({
  onAllow,
  onDismiss,
}: {
  onAllow: () => void
  onDismiss: () => void
}) {
  return (
    <aside
      role="status"
      aria-label="Lock with your screen"
      className="flex flex-none flex-wrap items-center gap-x-3 gap-y-2 border-b border-border-subtle bg-surface-2 px-4 py-2 text-[14px]"
    >
      <MonitorSmartphone size={16} aria-hidden="true" className="flex-none text-text-3" />
      <span className="min-w-0 flex-1 text-text-2">
        Let Viko lock when your screen locks. It learns only locked/idle, nothing else.
      </span>
      <span className="flex gap-1">
        <Button variant="primary" size="sm" onClick={onAllow}>
          Allow
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Not now
        </Button>
      </span>
    </aside>
  )
}

/** Mounts the one-time banner while the controller says it is due; renders nothing otherwise. */
export function IdleLockPrompt() {
  const { controller, status } = useAppLockStatus()
  if (!controller || !status?.promptIdle) return null
  return (
    <IdleDetectionPrompt
      onAllow={() => void controller.allowIdleDetection()}
      onDismiss={() => void controller.dismissIdlePrompt()}
    />
  )
}

export function useAppLockStatus() {
  const c = useSyncExternalStore(appLock.subscribe, appLock.get, appLock.get)
  const status = useSyncExternalStore(
    c?.subscribe ?? noop,
    c?.snapshot ?? none,
    c?.snapshot ?? none,
  )
  return { controller: c, status }
}
const noop = () => () => {}
const none = () => undefined

export function AppLock() {
  const b = useBackend()
  const controller = useMemo(
    () =>
      new AppLockController({
        // unlock-via-mac: while content is locked, this ONE tap also unlocks it (PRF or the Mac).
        verify: () => b.lock.appUnlock(passkeyUnlock(passkeys, async () => b.content?.accountId())),
        // passkey-budget WP1: lock follows the OS (a daemon-signed OS unlock proof, no passkey).
        presenceUnlock: (since) =>
          tryPresenceUnlock(
            {
              accountId: async () => b.content?.accountId(),
              deviceKeyId: async () => (await loadDevice())?.deviceKeyId,
              request: async (machineId, payload) =>
                (await b.dispatch('presence.unlock_proof', machineId, payload)).result,
              sessionOnly: isSessionOnly,
            },
            since,
          ),
        persist: persistAppLock,
        mobile: isMobileDevice(),
        readPresence: (machineId) => b.api.machinePresence(machineId),
        onWork: (cb) => {
          const off = b.hub.onWork(cb)
          return () => void off()
        },
      }),
    [b],
  )
  const lock = controller.lock
  const [locked, setLocked] = useState(lock.locked)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [presence, setPresence] = useState(false)
  useEffect(() => {
    if (locked) void loadPresenceBinding().then((binding) => setPresence(!!binding))
  }, [locked])
  useEffect(() => {
    appLock.set(controller)
    const off = lock.subscribe(setLocked)
    let disposed = false
    let stop: (() => void) | undefined
    void controller
      .start()
      .then((s) => {
        if (disposed) s()
        else stop = s
      })
      .catch(() => {})
    const detach = lock.attach(window, document)
    return () => {
      disposed = true
      off()
      detach()
      stop?.()
      if (appLock.get() === controller) appLock.set(undefined)
    }
  }, [controller, lock])
  const unlock = async (auto = false) => {
    setBusy(true)
    setError(undefined)
    try {
      await lock.unlock()
    } catch (e) {
      // passkey-budget WP2: a dismissed automatic Face ID prompt is silent (the button stays).
      if (!auto) setError(passkeyErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  const unlockRef = useRef(unlock)
  unlockRef.current = unlock
  // passkey-budget WP2: the phone's return to a locked Viko prompts Face ID by itself, once.
  const tried = useRef(false)
  useEffect(() => {
    if (!locked) {
      tried.current = false
      return
    }
    const attempt = () => {
      if (
        !shouldAutoPrompt({
          locked: lock.locked,
          mobile: lock.mobile,
          visible: document.visibilityState === 'visible',
          triedThisLock: tried.current,
        })
      )
        return
      tried.current = true
      void unlockRef.current(true)
    }
    attempt()
    document.addEventListener('visibilitychange', attempt)
    return () => document.removeEventListener('visibilitychange', attempt)
  }, [locked, lock])
  if (!locked) return null
  return (
    <AppLockView presence={presence} busy={busy} error={error} onUnlock={() => void unlock()} />
  )
}
